import dayjs from 'dayjs';

import { Calendar } from '@/_shared/components/ui/calendar';

import { CalendarPopover } from './CalendarPopover';
import { ObservedRange } from './ObservedRange';

/** A single calendar date fused into an InputGroup — the chip shows the formatted day (dayjs) and opens
 *  a Popover calendar. Value is a `YYYY-MM-DD` string (or null when unset).
 *
 *  `min`/`max` are the column's OBSERVED range, when it has been measured. They open the calendar on the
 *  data rather than on today and caption the span, so a date rule stops being the one type that shows the
 *  customer nothing (§10.4 gives a timestamp bounds and no control). They are ADVISORY: dates outside the
 *  observed range stay pickable, because the range describes what has been seen, not what may be asked
 *  for — a rule about the future is legitimate and must not be blocked by yesterday's measurement. */
export function DateValue({
  value,
  onChange,
  ariaLabel,
  min,
  max,
}: {
  value: string | null | undefined;
  onChange: (date: string | null) => void;
  ariaLabel: string;
  min?: string;
  max?: string;
}) {
  const selected = value ? dayjs(value).toDate() : undefined;
  // Open on the selection, else on the observed range's end — never a blank month a thousand rows away
  // from the data.
  const defaultMonth = selected ?? (max ? dayjs(max).toDate() : undefined);

  return (
    <CalendarPopover
      ariaLabel={ariaLabel}
      hasValue={!!value}
      label={value ? dayjs(value).format('MMM D, YYYY') : 'Pick a date'}
    >
      {(close) => (
        <div className="flex flex-col gap-2">
          <Calendar
            mode="single"
            selected={selected}
            {...(defaultMonth ? { defaultMonth } : {})}
            onSelect={(date) => {
              onChange(date ? dayjs(date).format('YYYY-MM-DD') : null);
              close();
            }}
            autoFocus
          />
          <ObservedRange min={min} max={max} />
        </div>
      )}
    </CalendarPopover>
  );
}
